import type { UtmFields } from "./analytics-utm";
import { assertAllowedAnalyticsEvent } from "./analytics-taxonomy";

export const GA4_CONSENT_DEFAULT_DENIED = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
} as const;

/** Analytics-only grant: ad_* stay denied. */
export const GA4_CONSENT_ANALYTICS_GRANTED = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "granted",
} as const;

export type Ga4Gtag = (...args: unknown[]) => void;

/** HTML-safe JSON for inline script embedding (</script>, U+2028/U+2029). */
export function jsonForInlineScript(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, "\\u003c")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

export function buildGa4ConfigParams(utm: UtmFields | null): Record<string, string | boolean> {
  const params: Record<string, string | boolean> = { send_page_view: true };
  if (utm) {
    for (const [key, value] of Object.entries(utm)) {
      if (typeof value === "string" && value !== "") {
        params[key] = value;
      }
    }
  }
  const stringParams: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === "string") stringParams[key] = value;
  }
  assertAllowedAnalyticsEvent("page_view", stringParams);
  return params;
}

export function buildGa4ConfigOptionsScript(utm: UtmFields | null): string {
  return jsonForInlineScript(buildGa4ConfigParams(utm));
}

export function applyGa4ConsentGranted(
  gtag: Ga4Gtag,
  measurementId: string,
  utm: UtmFields | null,
): void {
  gtag("consent", "update", { ...GA4_CONSENT_ANALYTICS_GRANTED });
  gtag("config", measurementId, buildGa4ConfigParams(utm));
}

/**
 * Re-applies consent + config when gtag is already on the page (e.g. re-consent after revoke).
 * Returns true when handled in JS; false when caller should inject gtag scripts.
 */
export function initOrRefreshGa4(measurementId: string, utm: UtmFields | null): boolean {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    applyGa4ConsentGranted(window.gtag, measurementId, utm);
    return true;
  }
  return false;
}

export function buildGa4InlineInitScript(measurementId: string, utm: UtmFields | null): string {
  const idJson = jsonForInlineScript(measurementId);
  const configJson = buildGa4ConfigOptionsScript(utm);
  const denied = jsonForInlineScript(GA4_CONSENT_DEFAULT_DENIED);
  const granted = jsonForInlineScript(GA4_CONSENT_ANALYTICS_GRANTED);
  return (
    `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}` +
    `gtag('consent','default',${denied});` +
    `gtag('js',new Date());` +
    `gtag('consent','update',${granted});` +
    `gtag('config',${idJson},${configJson});`
  );
}
